import {
  DEFAULT_TIMELINE_CALLBACKS,
  getViewRelevantScopeData,
  getFieldsAndItemsSourceDataForWorkspaceMode,
  getGraphNodeOrGroupLabelInWorkspaceMode,
  ViewModelSourceData,
  DEFAULT_TIMELINE_VIEW_MODEL_SOURCE_DATA,
  TimelineCallbacks,
  TimelineViewSettings,
} from '@ardoq/timeline2024';
import {
  APIComponentAttributes,
  APITagAttributes,
  ArdoqId,
  ViewIds,
} from '@ardoq/api-types';
import { componentOrDescendantsAreIncludedInContextByFilter } from '@ardoq/component-interface';
import { isPresentationMode } from 'env';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { Features, hasFeature } from '@ardoq/features';
import { emptyReferenceFilterContext } from '@ardoq/filter-interface';
import {
  getGraphInterfaceWithModelInterfaces,
  getLabelsInterfaceWithModelInterfaces,
  getRelevantComponentIds,
  getRepresentationInterfaceWithModelInterfaces,
  isGraphModelValid,
} from '@ardoq/graph';
import { dateRangeOperations } from '@ardoq/date-range';
import { ComponentByIdSortDependencies, isTruthy } from '@ardoq/common-helpers';
import { buildGraph } from 'modelInterface/graph/buildGraphForWorkspaceMode';
import { ContextShape, GraphModelShape } from '@ardoq/data-model';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { getTimelineSpecificInterfaceWithModelInterfaces } from '../viewModel/timelineSpecificInterface';
import { Locale } from '@ardoq/locale';
import type { I18nLocale } from '@ardoq/i18n';
import type { ErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import type { ActiveFilterState } from 'streams/filters/types';
import type { FieldsState } from 'streams/fields/fields$';
import type { GroupingRulesState } from 'streams/grouping/groupingRules$';
import type { WorkspacesState } from 'streams/workspaces/workspaces$';
import type { StreamCollection } from 'streams/utils/streamUtils';
import {
  getComponentGroupRawItems,
  getTimelineViewGraphHierarchyForWorkspaceMode,
} from '../viewModel/utils';
import { getTimelineTrackingCallbacks } from '../performanceTrackingUtils';

export type WorkspaceModeTimelineSourceDataArgs = {
  context: ContextShape;
  viewSettings: TimelineViewSettings;
  graphModel: GraphModelShape;
  locale: Locale;
  i18nLocale: I18nLocale;
  errorInfoBoxLabels: ErrorInfoBoxLabels;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
  /**
   * The build reads the state below through the model interfaces rather than
   * from these arguments. They are here so it recomputes when that state changes.
   */
  modelResetParameters: WorkspaceModeTimelineModelResetParameters;
  /** Duration tracking session started when the source data args were emitted. */
  trackingId?: string;
};

/**
 * State the workspace mode build reads through the model interfaces: filters
 * and the formatting they drive in the start set and the label and
 * representation interfaces, grouping rules in the traversal, and fields, tags
 * and workspaces in the view relevant scope data.
 */
export type WorkspaceModeTimelineModelResetParameters = {
  activeFilterState: ActiveFilterState;
  groupingRules: GroupingRulesState;
  fields: FieldsState;
  tags: StreamCollection<APITagAttributes>;
  workspaces: WorkspacesState;
};

/**
 * If context component has child nodes, show it and its children. Otherwise,
 * show context component siblings or workspace root components.
 */
const getTimelineStartSetForWorkspaceMode = (
  contextComponentId: ArdoqId,
  contextWorkspaceId: ArdoqId,
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>
) => {
  const contextComponentHasChildNodes =
    contextComponentId &&
    Boolean(childrenByParent.get(contextComponentId)?.length);

  const componentIds = contextComponentHasChildNodes
    ? [contextComponentId]
    : getRelevantComponentIds({
        componentId: contextComponentId,
        workspaceId: contextWorkspaceId,
        childrenByParent,
      });

  return contextComponentHasChildNodes
    ? componentIds
    : componentIds.filter(componentOrDescendantsAreIncludedInContextByFilter);
};

export const buildWorkspaceModeViewModelSourceData = (
  state: ViewModelSourceData,
  {
    context: {
      componentId: contextComponentId,
      workspaceId: contextWorkspaceId,
      sort,
    },
    viewSettings,
    graphModel: graph,
    locale: userLocale,
    i18nLocale,
    errorInfoBoxLabels,
    childrenByParent,
    trackingId,
  }: WorkspaceModeTimelineSourceDataArgs
): ViewModelSourceData => {
  const { viewInterface } = state;

  const { getAllGroupingRules, isComponent } = viewInterface;

  const userDefinedGrouping = getAllGroupingRules();
  const hasGrouping = Boolean(userDefinedGrouping.length);

  const updatedState: ViewModelSourceData = {
    ...state,
    contextComponentId,
    graph,
    contextWorkspaceId,
    hasGroupingStyles: hasGrouping,
    sourceDataViewSettings: viewSettings,
    i18nLocale,
    errorInfoBoxLabels,
  };

  if (!contextWorkspaceId || !isGraphModelValid(viewInterface, graph)) {
    // if graph is invalid, typically this means the graphModel$ has not updated itself with the latest state
    // of the References collection, we therefore avoid rebuilding the graph based on other updates
    return updatedState;
  }

  const startSet = getTimelineStartSetForWorkspaceMode(
    contextComponentId,
    contextWorkspaceId,
    childrenByParent
  );

  const traversalAndGroupingResult = buildGraph({
    graphInterface: viewInterface,
    viewId: ViewIds.TIMELINE,
    graph,
    startSet,
    groupingRules: userDefinedGrouping,
    traverseOptions: {
      maxDegreesIncoming: 0,
      maxDegreesOutgoing: 0,
      maxDegrees: 10,
      isParentRelationAsReference: true,
      outgoingReferenceTypes: null,
      incomingReferenceTypes: null,
      excludedComponentIds: new Set(),
    },
    useNewGrouping: viewSettings.useNewGrouping,
    i18nLocale,
    ctx: emptyReferenceFilterContext(),
  });

  const componentIds = [
    ...traversalAndGroupingResult.componentMap.keys(),
    ...getComponentGroupRawItems(
      traversalAndGroupingResult.groupMap,
      new Set(traversalAndGroupingResult.componentMap.keys())
    ).map(({ modelId }) => modelId),
  ].filter(viewInterface.isComponent);

  const componentAttributesEntries = componentIds
    .map(id => viewInterface.get(id))
    .filter(isTruthy)
    .map(componentAttributes => [componentAttributes._id, componentAttributes]);

  const componentAttributesById = Object.fromEntries(
    componentAttributesEntries
  );

  const sortDependencies: ComponentByIdSortDependencies = {
    componentAttributesById,
    sort,
    isDateRangeField: dateRangeOperations.isCombinedDateRangeFieldNames(
      sort.attr
    ),
    userLocale,
  };

  const { viewGraphHierarchy, allComponentIds } =
    getTimelineViewGraphHierarchyForWorkspaceMode({
      startSet,
      hasUserDefinedGroups: hasGrouping,
      traversalAndGroupingResult,
      childrenByParent,
    });

  viewGraphHierarchy.forEach(({ rawGraphItem, graphItem }) => {
    if (rawGraphItem) {
      graphItem.setItemLabels(
        getGraphNodeOrGroupLabelInWorkspaceMode({
          labelsInterface: viewInterface,
          rawGraphItem,
          isComponent,
        })
      );
    }
  });

  const { hasRootGroupsInContextOnlyFeature } = viewInterface.features;

  const {
    viewRelevantWorkspacesScopeData,
    viewRelevantComponentsScopeData,
    workspaceAndFieldsRegistry,
  } = getViewRelevantScopeData({
    allComponentIds,
    viewInterface,
    childrenByParent,
  });

  // final tweaks of the view model scope and source data
  const fieldsAndItemsSourceData = getFieldsAndItemsSourceDataForWorkspaceMode({
    viewGraphHierarchy,
    contextComponentId,
    viewSettings,
    viewRelevantWorkspacesScopeData,
    viewRelevantComponentsScopeData,
    hasGrouping,
    hasRootGroupsInContextOnlyFeature,
    workspaceAndFieldsRegistry,
  });

  const { errors, hasClones } = traversalAndGroupingResult;

  const metrics = {
    componentCount: allComponentIds.length,
    referenceCount: 0, // Timeline does not show references
    workspaceCount: workspaceAndFieldsRegistry.size,
  };

  const trackingCallbacks = trackingId
    ? getTimelineTrackingCallbacks(trackingId, metrics)
    : undefined;

  return {
    ...updatedState,
    viewInterface: {
      ...updatedState.viewInterface,
      trackingCallbacks,
    },
    errors,
    hasClones,
    viewRelevantComponentsScopeData,
    workspaceAndFieldsRegistry,
    fieldsAndItemsSourceData,
    sortDependencies,
    contextComponentId,
    isViewEmpty: !fieldsAndItemsSourceData.startSetNodes.length,
  };
};

/**
 * Feed it with scope data, grouping rules and conditional formatting, and use the
 * get[concern]InterfaceForEnhancedScopeData functions to avoid the BB models
 *
 * @returns methods that are needed for composing the view model source data
 */
const getScopeDataOrModelInterfaces = () => {
  const graphInterface = getGraphInterfaceWithModelInterfaces();
  const labelsInterface = getLabelsInterfaceWithModelInterfaces();
  const representationInterface =
    getRepresentationInterfaceWithModelInterfaces();
  const viewSpecificInterface =
    getTimelineSpecificInterfaceWithModelInterfaces();
  return {
    ...graphInterface,
    ...labelsInterface,
    ...representationInterface,
    ...viewSpecificInterface,
  };
};
export const getInitialWorkspaceModeSourceDataState = (
  viewInstanceId: string
): ViewModelSourceData => {
  const timelineCallbacks: TimelineCallbacks = {
    ...DEFAULT_TIMELINE_CALLBACKS,
    openDateRangeViewSetting: simpleNotifier(),
    openMilestonesViewSetting: simpleNotifier(),
    onViewSettingsUpdate,
    isPresentationMode,
  };

  return {
    ...DEFAULT_TIMELINE_VIEW_MODEL_SOURCE_DATA,
    viewInterface: {
      ...getScopeDataOrModelInterfaces(),

      features: {
        hasRootGroupsInContextOnlyFeature: hasFeature(
          Features.ROOT_GROUPS_IN_CONTEXT_ONLY
        ),
        isModernizedStyle: false,
      },
      timelineCallbacks,
    },
    viewInstanceId,
    contextMenuDataAttributeName: COMPONENT_ID_ATTRIBUTE, // GLOBAL_HANDLER_ID_ATTRIBUTE for Discover
  };
};
